import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { ICategoryPath } from '@/Module/Grade/Model/DataModel';
import { prepareLessonListFromCourse, prepareLessonRelatedList } from '@/Module/Lesson/Helper';
import type { IBookmark, ILesson, ILessonSection, ILessonSectionMedia2, ILike } from '@/Module/Lesson/Model/DataModel';
import {
  deleteBookmarkLessonByIdAction,
  deleteLikeLessonByIdAction,
  getBookMarkLessonByIdAction,
  getCourseByLessonIdAction,
  getCourseCategoryByCourseIdAction,
  getLessonByFriendlyTokenAction,
  getLessonByIdAction,
  getLessonListByCourseIdAction,
  getLikeLessonByIdAction,
  getRelatedLessonListByIdAction,
  getTeacherByLessonIdAction,
  postBookMarkLessonByIdAction,
  postLikeLessonByIdAction,
  updateLessonPooyanPlayTokenAction,
} from '@/Module/Lesson/Store/Action';
import { lessonSectionAdapter } from '@/Module/Lesson/Store/Adapter';
import type { ITeacher } from '@/Module/Teacher/Model/DataModel';

const LessonSectionSlice = createSlice({
  initialState: lessonSectionAdapter.getInitialState({
    isLoading: false,
    page: 0,
    rows: 12,
    total: 0,
    activeTabNumber: 1,
    selectedLessonSectionId: null as ILessonSection['id'] | null,
    relatedList: lessonSectionAdapter.getInitialState(),
    lessonSectionLesson: null as null | ILesson,
    playedPercentage: 0,
    lessonSectionMedia2: null as null | ILessonSectionMedia2,
    lessonSectionTeacher: null as null | ITeacher,
    lessonSectionCategoryCategoryId: null as null | ICategoryPath['id'],
    canReport: false,
    forceAsiatech: false,
    likeLoading: false,
    bookmarkLoading: false,
    relatedSubscriptionId: 0,
    like: null as null | ILike,
    bookmark: null as null | IBookmark,
  }),
  name: 'lessonSection',
  reducers: {
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload;
    },
    nextPage: state => {
      state.page += 1;
    },
    setSelectedLessonSectionId: (state, { payload }: PayloadAction<ILessonSection['id'] | null>) => {
      state.selectedLessonSectionId = payload;
    },
    setCanReport: (state, { payload }: PayloadAction<boolean>) => {
      state.canReport = payload;
    },
    setForceAsiatech: (state, { payload }: PayloadAction<boolean>) => {
      state.forceAsiatech = payload;
    },
    setWatchedPercentage: (state, { payload }: PayloadAction<number>) => {
      state.playedPercentage = payload;
    },
    setRelatedSubscriptionId: (state, { payload }: PayloadAction<number>) => {
      state.relatedSubscriptionId = payload;
    },
    setActiveTabNumber: (state, { payload }: PayloadAction<number>) => {
      state.activeTabNumber = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getLessonListByCourseIdAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getLessonListByCourseIdAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getLessonListByCourseIdAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.total = payload.total;

        if (state.page === 0) {
          lessonSectionAdapter.setAll(state, payload.data);
        } else {
          lessonSectionAdapter.addMany(state, payload.data);
        }
      });

    builder
      .addCase(getLessonByIdAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getLessonByIdAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getLessonByIdAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        lessonSectionAdapter.setOne(state, payload);
      });

    builder
      .addCase(getLessonByFriendlyTokenAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getLessonByFriendlyTokenAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getLessonByFriendlyTokenAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        lessonSectionAdapter.setOne(state, payload);
      });

    builder
      .addCase(getRelatedLessonListByIdAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getRelatedLessonListByIdAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getRelatedLessonListByIdAction.fulfilled, (state, { payload, meta }) => {
        state.isLoading = false;

        if (state.page === 0) {
          lessonSectionAdapter.setAll(state.relatedList, prepareLessonRelatedList(payload, meta.arg.id));
        } else {
          lessonSectionAdapter.addMany(state.relatedList, payload);
        }
      });

    builder
      .addCase(getLikeLessonByIdAction.pending, state => {
        state.likeLoading = false;
      })
      .addCase(getLikeLessonByIdAction.rejected, state => {
        state.likeLoading = false;
      })
      .addCase(getLikeLessonByIdAction.fulfilled, (state, { payload }) => {
        state.like = payload;
      });

    builder
      .addCase(postLikeLessonByIdAction.pending, state => {
        state.likeLoading = true;
      })
      .addCase(postLikeLessonByIdAction.rejected, state => {
        state.likeLoading = false;
      })
      .addCase(postLikeLessonByIdAction.fulfilled, (state, { payload }) => {
        state.likeLoading = false;
        state.like = payload;
      });

    builder
      .addCase(deleteLikeLessonByIdAction.pending, state => {
        state.likeLoading = true;
      })
      .addCase(deleteLikeLessonByIdAction.rejected, state => {
        state.likeLoading = false;
      })
      .addCase(deleteLikeLessonByIdAction.fulfilled, state => {
        state.likeLoading = false;
        state.like = null;
      });

    builder
      .addCase(getBookMarkLessonByIdAction.pending, state => {
        state.bookmarkLoading = false;
      })
      .addCase(getBookMarkLessonByIdAction.rejected, state => {
        state.bookmarkLoading = false;
      })
      .addCase(getBookMarkLessonByIdAction.fulfilled, (state, { payload }) => {
        state.bookmark = payload;
      });

    builder
      .addCase(postBookMarkLessonByIdAction.pending, state => {
        state.bookmarkLoading = true;
      })
      .addCase(postBookMarkLessonByIdAction.rejected, state => {
        state.bookmarkLoading = false;
      })
      .addCase(postBookMarkLessonByIdAction.fulfilled, (state, { payload }) => {
        state.bookmarkLoading = false;
        state.bookmark = payload;
      });

    builder
      .addCase(deleteBookmarkLessonByIdAction.pending, state => {
        state.bookmarkLoading = true;
      })
      .addCase(deleteBookmarkLessonByIdAction.rejected, state => {
        state.bookmarkLoading = false;
      })
      .addCase(deleteBookmarkLessonByIdAction.fulfilled, state => {
        state.bookmarkLoading = false;
        state.bookmark = null;
      });

    // builder
    //   .addCase(getLessonSectionMedia2Action.pending, state => {
    //     state.isLoading = true;
    //   })
    //   .addCase(getLessonSectionMedia2Action.rejected, state => {
    //     state.isLoading = false;
    //   })
    //   .addCase(getLessonSectionMedia2Action.fulfilled, (state, { payload }) => {
    //     state.isLoading = false;
    //     state.lessonSectionMedia2 = payload;
    //   });

    builder.addCase(getCourseByLessonIdAction.fulfilled, (state, { payload }) => {
      // eslint-disable-next-line prefer-destructuring
      state.lessonSectionLesson = prepareLessonListFromCourse([payload])[0];
    });

    builder.addCase(getTeacherByLessonIdAction.fulfilled, (state, { payload }) => {
      state.lessonSectionTeacher = payload;
    });

    builder.addCase(getCourseCategoryByCourseIdAction.fulfilled, (state, { payload }) => {
      state.lessonSectionCategoryCategoryId = payload?.[0]?.categoryCategoryId ?? null;
    });

    // get play token
    // TODO complete for more extra yourself
    builder.addCase(updateLessonPooyanPlayTokenAction.fulfilled, (state, { payload }) => {
      if (!state.selectedLessonSectionId) return;
      if (!payload?.player_token) return;

      lessonSectionAdapter.updateOne(state, {
        id: state.selectedLessonSectionId,
        changes: {
          mediaLink2: payload?.player_token,
        },
      });
    });
  },
});

export const {
  nextPage: lessonSectionNextPageAction,
  setActiveTabNumber: lessonSectionSetActiveTabNumber,
  setSelectedLessonSectionId: lessonSectionSetSelectedLessonSectionIdAction,
  setCanReport: setCanReportAction,
  setForceAsiatech: setForceAsiatechAction,
  setRelatedSubscriptionId: setRelatedSubscriptionAction,
  setWatchedPercentage,
} = LessonSectionSlice.actions;

export default LessonSectionSlice;
